import { registerIndicator, type KLineData } from 'klinecharts'
import type { PlotType } from '@edge/charts-indicators'

import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { type Num, type OhlcvBar, emaSeries, rollingMean } from './seriesMath'

export type MacdSource = 'close' | 'open' | 'high' | 'low' | 'hl2' | 'hlc3' | 'ohlc4'
export type MacdMaType = 'ema' | 'sma'

/** Positional defaults; mirrors the catalog's MACD params (fast, slow, signal,
 *  source, oscillator MA, signal MA). Older saved charts carry only the first
 *  three, so every slot is read with its default as the fallback. */
const DEFAULTS = [12, 26, 9, 'close', 'ema', 'ema'] as const

const TRANSPARENT = 'rgba(0,0,0,0)'

function sourceValue(bar: OhlcvBar, source: MacdSource): number {
  switch (source) {
    case 'open':
      return bar.open
    case 'high':
      return bar.high
    case 'low':
      return bar.low
    case 'hl2':
      return (bar.high + bar.low) / 2
    case 'hlc3':
      return (bar.high + bar.low + bar.close) / 3
    case 'ohlc4':
      return (bar.open + bar.high + bar.low + bar.close) / 4
    default:
      return bar.close
  }
}

function average(values: readonly Num[], period: number, type: MacdMaType): Num[] {
  return type === 'sma' ? rollingMean(values, period) : emaSeries(values, period)
}

/**
 * MACD line = fast MA - slow MA of the source, signal = MA of the MACD line,
 * histogram = MACD - signal. The histogram is NOT doubled (the klinecharts
 * built-in multiplies it by 2).
 */
export function computeMACD(
  bars: readonly OhlcvBar[],
  fast: number,
  slow: number,
  signal: number,
  source: MacdSource,
  oscMaType: MacdMaType,
  signalMaType: MacdMaType,
): Array<{ dif?: number; dea?: number; macd?: number }> {
  const values = bars.map((b) => sourceValue(b, source))
  const fastMa = average(values, fast, oscMaType)
  const slowMa = average(values, slow, oscMaType)
  const dif = values.map((_, i) =>
    fastMa[i] != null && slowMa[i] != null ? fastMa[i]! - slowMa[i]! : undefined,
  )
  const dea = average(dif, signal, signalMaType)
  return bars.map((_, i) => {
    const out: { dif?: number; dea?: number; macd?: number } = {}
    if (dif[i] != null) out.dif = dif[i]
    if (dea[i] != null) {
      out.dea = dea[i]
      out.macd = dif[i]! - dea[i]!
    }
    return out
  })
}

type HistBarStyle = {
  upColor?: string
  downColor?: string
  noChangeColor?: string
  growAboveColor?: string
  fallAboveColor?: string
  growBelowColor?: string
  fallBelowColor?: string
  visible?: boolean
  plotType?: PlotType
}

/**
 * Histogram bar colour by sign and direction: strong while the bar grows away
 * from zero, pale while it shrinks back. Reads the per-instance style first
 * (`indicator.styles.bars[0]`, where `buildBarStyle` puts the user's colours);
 * klinecharts hands the THEME default as `defaultStyles`, which alone would
 * discard the user's pick (see volumeIndicator's `barColorStyle`).
 */
export function macdHistogramColor(
  data: {
    prev: { indicatorData?: { macd?: number } }
    current: { indicatorData?: { macd?: number } }
  },
  indicator: { styles?: { bars?: HistBarStyle[] } },
  defaultStyles: { bars: HistBarStyle[] },
): { color: string; borderColor: string; style: 'fill' } {
  const bars = indicator?.styles?.bars?.[0] ?? defaultStyles.bars[0]
  const done = (color: string | undefined) => {
    const c = color ?? TRANSPARENT
    return { color: c, borderColor: c, style: 'fill' as const }
  }
  if (bars.visible === false) return done(TRANSPARENT)
  // Drawn as a line (the Style tab's Plot type), one colour: four per bar would
  // cut the line into coloured pieces.
  if (bars.plotType === 'line') return done(bars.noChangeColor)
  const cur = data.current.indicatorData?.macd
  const prev = data.prev.indicatorData?.macd
  if (cur == null || cur === 0) return done(bars.noChangeColor)
  // No previous bar: count it as growing (it moved away from nothing).
  if (cur > 0) {
    const growing = prev == null || cur >= prev
    return done(
      growing ? (bars.growAboveColor ?? bars.upColor) : (bars.fallAboveColor ?? bars.upColor),
    )
  }
  const growing = prev != null && cur > prev
  return done(
    growing ? (bars.growBelowColor ?? bars.downColor) : (bars.fallBelowColor ?? bars.downColor),
  )
}

/**
 * Register our MACD under the built-in's name, replacing it (same move as
 * `registerVolumeIndicator` for VOL). Saved charts keep working: the name and
 * figure keys are unchanged and short calcParams arrays fall back per slot.
 */
export const registerMacdIndicator = registerOnce(() => {
  registerIndicator({
    name: 'MACD',
    shortName: 'MACD',
    calcParams: [...DEFAULTS],
    figures: [
      // Histogram first so it paints under the two lines.
      {
        key: 'macd',
        title: 'Histogram: ',
        type: 'bar',
        baseValue: 0,
        styles: macdHistogramColor as never,
      },
      { key: 'dif', title: 'MACD: ', type: 'line' },
      { key: 'dea', title: 'Signal: ', type: 'line' },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams: unknown[] }) => {
      const p = indicator.calcParams ?? []
      const num = (i: 0 | 1 | 2) => {
        const n = Number(p[i])
        return Number.isFinite(n) && n >= 1 ? Math.round(n) : DEFAULTS[i]
      }
      return computeMACD(
        dataList,
        num(0),
        num(1),
        num(2),
        (p[3] as MacdSource | undefined) ?? DEFAULTS[3],
        (p[4] as MacdMaType | undefined) ?? DEFAULTS[4],
        (p[5] as MacdMaType | undefined) ?? DEFAULTS[5],
      )
    },
  } as IndicatorTemplateParam)
})
